import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import CloseButton from '../CloseButton'
import { isTablet } from 'react-device-detect'
import { cn } from '../../utils/cn'

const imageMaxSizeClassName = cn({
  'max-h-[calc(80vh-120px)] max-w-full': isTablet,
  'max-h-[80vh] max-w-[80vw]': !isTablet,
})

interface LightboxProps {
  isOpen: boolean
  onClose: () => void
  currentImage: string
  images: string[]
  currentIndex: number
  onPrevious: () => void
  onNext: () => void
  onSelectImage: (index: number) => void
  inventoryNumber?: string
  altText?: string
}

const Lightbox = ({
  isOpen,
  onClose,
  currentImage,
  images,
  currentIndex,
  onPrevious,
  onNext,
  onSelectImage,
  inventoryNumber,
  altText = 'Image',
}: LightboxProps) => {
  const { t } = useTranslation()
  const [isClosing, setIsClosing] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      setIsClosing(false)
    } else if (shouldRender) {
      setIsClosing(true)
      const timer = setTimeout(() => {
        setShouldRender(false)
        setIsClosing(false)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  const handleClose = () => {
    onClose()
  }

  // ESC key to close lightbox
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose()
      }
      if (event.key === 'ArrowLeft' && isOpen) {
        onPrevious()
      }
      if (event.key === 'ArrowRight' && isOpen) {
        onNext()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress)
      document.body.style.overflow = 'hidden' // Prevent background scrolling
      return () => {
        document.removeEventListener('keydown', handleKeyPress)
        document.body.style.overflow = 'unset'
      }
    }
  }, [isOpen, onClose, onPrevious, onNext])

  if (!shouldRender) return null

  return createPortal(
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 top-0 z-[10000] flex h-screen w-screen cursor-pointer items-center justify-center bg-[rgba(0,0,0,0.9)]',
        {
          'animate-[simple-fade-out_1s_ease]': isClosing,
          'animate-[simple-fade-in_1s_ease]': !isClosing,
        },
      )}
      onClick={handleClose}
    >
      <div
        className="relative flex h-screen w-screen cursor-default flex-col items-center justify-center max-xs:h-screen max-xs:w-screen xxl:h-screen xxl:w-screen xxl:[&_.close-button]:right-[4%] xxl:[&_.close-button]:top-[15vh] xxl:[&_.close-button]:h-[112px] xxl:[&_.close-button]:w-[112px] xxl:[&_.close-button_svg]:h-[50px] xxl:[&_.close-button_svg]:w-[50px]"
        onClick={e => e.stopPropagation()}
      >
        {images.length > 1 && (
          <>
            <button
              className="absolute left-[5vh] top-1/2 flex h-[80px] w-[80px] cursor-pointer items-center justify-center rounded-[50%] border-none bg-transparent p-0 text-[64px] leading-[0] text-white [font-weight:lighter] [transform:translateY(-50%)] [transition:all_0.2s_ease] max-xs:left-[10px] max-xs:h-[50px] max-xs:w-[50px] xxl:left-[10px]"
              onClick={onPrevious}
            >
              <SVG src="/icons/lb_arrow_left.svg" />
            </button>
            <button
              className="absolute right-[5vh] top-1/2 flex h-[80px] w-[80px] cursor-pointer items-center justify-center rounded-[50%] border-none bg-transparent p-0 text-[64px] leading-[0] text-white [font-weight:lighter] [transform:translateY(-50%)] [transition:all_0.2s_ease] max-xs:right-[10px] max-xs:h-[50px] max-xs:w-[50px] xxl:right-[10px]"
              onClick={onNext}
            >
              <SVG src="/icons/lb_arrow_right.svg" />
            </button>
          </>
        )}

        <div className="flex max-h-full max-w-full flex-col items-center">
          <img
            className={cn(
              imageMaxSizeClassName,
              'mb-[20px] rounded-[8px] object-contain max-xs:max-h-[50vh] max-xs:max-w-[90vw] xxl:max-h-[calc(90vh-120px)] xxl:max-w-[80vw]',
            )}
            src={currentImage.replace('small_', '')}
            alt={altText}
          />

          <div className="flex flex-col items-center gap-[16px]">
            {inventoryNumber && (
              <p className="m-0 text-center text-[16px] text-white">
                {t('inventoryNumber')}: {inventoryNumber}
              </p>
            )}

            {images.length > 1 && (
              <div className="flex justify-center gap-[12px] max-xs:gap-[8px]">
                {images.map((image, index) => (
                  <img
                    key={index}
                    className={cn(
                      imageMaxSizeClassName,
                      'mb-[20px] h-[90px] w-auto cursor-pointer rounded-[4px] border-[2px] border-solid border-transparent object-cover [transition:all_0.2s_ease] hover:[transform:scale(1.1)] max-xs:h-[50px] max-xs:max-h-[50vh] max-xs:w-[50px] max-xs:max-w-[90vw] xxl:max-h-[calc(90vh-120px)] xxl:max-w-[80vw] [&.selected]:border-[2px] [&.selected]:border-solid [&.selected]:border-white [&.selected]:shadow-[0_0_10px_rgba(255,255,255,0.5)]',
                      { selected: index === currentIndex },
                    )}
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    onClick={() => onSelectImage(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <CloseButton onClick={onClose} className="close-button" />
      </div>
    </div>,
    document.body,
  )
}

export default Lightbox
